import { createContext, ReactNode, useCallback, useEffect, useReducer } from 'react';
// @types
import { ActionMap, AwsAuthContextType, AuthState, AuthUser } from '../@types/auth';
// AWS SDK
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  GetUserCommand,
  AdminSetUserPasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
// ----------------------------------------------------------------------
import { CognitoUserInterface } from 'src/@types/types';
import { trackAuthError, setSentryUser, clearSentryUser } from 'src/utils/sentryUtils';
import { awsconfig } from 'src/config';
import { createLoginEvent } from 'src/repository/userEvent';
import { checkUserService } from 'src/utils/checkUserService';

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isAdmin: false,
  user: null,
  organization: null,
  jwtToken: null,
};

// AWS SDK Cognito Client
const cognitoClient = new CognitoIdentityProviderClient({
  region: awsconfig.aws_cognito_region,
});

// Helper function to decode JWT token and extract groups (base64url-safe)
const getGroupsFromToken = (idToken: string): string[] => {
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return [];
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return payload['cognito:groups'] || [];
  } catch (error) {
    console.error('Failed to decode token:', error);
    return [];
  }
};

enum Types {
  auth = 'AUTHENTICATE',
  logout = 'LOGOUT',
}

type AwsAuthPayload = {
  [Types.auth]: {
    isInitialized: boolean;
    isAuthenticated: boolean;
    isAdmin: boolean;
    user: AuthUser;
    jwtToken: string | null;
  };
  [Types.logout]: undefined;
};

type AwsActions = ActionMap<AwsAuthPayload>[keyof ActionMap<AwsAuthPayload>];

const reducer = (state: AuthState, action: AwsActions) => {
  try {
    if (action.type === 'AUTHENTICATE') {
      const { isAuthenticated, isAdmin, user, jwtToken } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        isAdmin,
        user,
        organization: user?.attributes['custom:organization'] ?? null,
        jwtToken,
      };
    }
    if (action.type === 'LOGOUT') {
      return {
        ...state,
        isAuthenticated: false,
        isAdmin: false,
        user: null,
        jwtToken: null,
      };
    }
    return state;
  } catch (error) {
    localStorage.clear();
    return {
      ...state,
      isAuthenticated: false,
      isAdmin: false,
      user: null,
      jwtToken: null,
    };
  }
};

const AuthContext = createContext<AwsAuthContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getUserAttributes = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return null;
    }

    try {
      const command = new GetUserCommand({
        AccessToken: accessToken,
      });
      const response = await cognitoClient.send(command);

      // Convert AWS SDK response to CognitoUserInterface format
      const attributes: CognitoUserInterface['attributes'] = {
        email: '',
        sub: '',
        'custom:organization': '',
      };

      // Map user attributes
      response.UserAttributes?.forEach((attr) => {
        if (attr.Name && attr.Value) {
          attributes[attr.Name as keyof CognitoUserInterface['attributes']] = attr.Value;
        }
      });

      const user: CognitoUserInterface = {
        username: response.Username || attributes.sub || '',
        attributes,
        challengeName: '',
        challengeParam: {},
      };

      return user;
    } catch (error) {
      console.error('Failed to get user attributes:', error);
      return null;
    }
  }, []);

  const getSession = useCallback(async () => {
    const idToken = localStorage.getItem('idToken');
    const cognitoUser = await getUserAttributes();

    let isGlobalAdmin = false;
    if (idToken && cognitoUser) {
      const groups = getGroupsFromToken(idToken);
      isGlobalAdmin = groups.includes('GlobalAdmins');
    }

    if (isGlobalAdmin) {
      setSentryUser({ id: 'global-admin', username: 'Global Admin' });
      dispatch({
        type: Types.auth,
        payload: {
          isInitialized: true,
          isAuthenticated: true,
          isAdmin: true,
          jwtToken: idToken,
          user: null,
        },
      });
    } else if (cognitoUser) {
      setSentryUser(cognitoUser);
      dispatch({
        type: Types.auth,
        payload: {
          isInitialized: true,
          isAuthenticated: true,
          isAdmin: false,
          user: cognitoUser,
          jwtToken: idToken,
        },
      });
    } else {
      dispatch({
        type: Types.auth,
        payload: {
          isInitialized: true,
          isAuthenticated: false,
          isAdmin: false,
          user: null,
          jwtToken: null,
        },
      });
    }
  }, [getUserAttributes]);

  const initial = useCallback(async () => {
    try {
      await getSession();
    } catch (error) {
      dispatch({
        type: Types.auth,
        payload: {
          isInitialized: true,
          isAuthenticated: false,
          isAdmin: false,
          user: null,
          jwtToken: null,
        },
      });
    }
  }, [getSession]);

  useEffect(() => {
    initial();
  }, [initial]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const clientId = awsconfig.aws_user_pools_web_client_id;

        // Log context to identify which Cognito App Client is used (no PII)
        console.info('[Auth] InitiateAuth context', {
          clientId,
          userPoolId: awsconfig.aws_user_pools_id,
          region: awsconfig.aws_cognito_region,
          authFlow: 'USER_PASSWORD_AUTH',
        });

        const command = new InitiateAuthCommand({
          ClientId: clientId,
          AuthFlow: 'USER_PASSWORD_AUTH',
          AuthParameters: {
            USERNAME: email,
            PASSWORD: password,
          },
        });

        const response = await cognitoClient.send(command);

        if (!response.AuthenticationResult) {
          throw new Error('Authentication failed');
        }

        // Store tokens in localStorage
        const { AccessToken, IdToken, RefreshToken } = response.AuthenticationResult;
        if (AccessToken) localStorage.setItem('accessToken', AccessToken);
        if (IdToken) localStorage.setItem('idToken', IdToken);
        if (RefreshToken) localStorage.setItem('refreshToken', RefreshToken);

        const cognitoUser = await getUserAttributes();

        // DynamoDBユーザーの確認・作成を同期的に実行
        // これにより、リダイレクト前にユーザーがDynamoDBに存在することが保証される
        if (cognitoUser) {
          const checkUserResult = await checkUserService(cognitoUser);
          if (!checkUserResult.success) {
            // ユーザー確認・作成に失敗した場合、ログインを失敗させる
            localStorage.clear();
            const error = new Error(checkUserResult.error || 'ユーザー情報の確認に失敗しました');
            (error as any).type = checkUserResult.errorType;
            throw error;
          }
        }

        await getSession();

        // Create login event asynchronously - don't block login success
        if (cognitoUser?.username) {
          createLoginEvent(cognitoUser.username).catch((error) => {
            console.warn('Failed to create login event, but login was successful:', error);
          });
        }
      } catch (error) {
        trackAuthError(error, 'login');
        try {
          const e = error as { name?: string; message?: string; stack?: string };
          console.error('[Auth] Login error:', e?.name || 'Error', e?.message || '', e);
          console.error('[Auth] Login error context', {
            clientId: awsconfig.aws_user_pools_web_client_id,
            userPoolId: awsconfig.aws_user_pools_id,
            region: awsconfig.aws_cognito_region,
            authFlow: 'USER_PASSWORD_AUTH',
          });
        } catch (_) {
          // noop: ensure logging never throws
        }
        dispatch({
          type: Types.auth,
          payload: {
            isInitialized: true,
            isAuthenticated: false,
            isAdmin: false,
            user: null,
            jwtToken: null,
          },
        });
        throw error;
      }
    },
    [getSession, getUserAttributes]
  );

  const logout = async () => {
    clearSentryUser();
    dispatch({ type: Types.logout });
    localStorage.clear();
  };

  const register = async (
    username: string,
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    organization: string
  ) => {
    const command = new SignUpCommand({
      ClientId: awsconfig.aws_user_pools_web_client_id,
      Username: username,
      Password: password,
      UserAttributes: [
        {
          Name: 'email',
          Value: email,
        },
        {
          Name: 'given_name',
          Value: firstName,
        },
        {
          Name: 'family_name',
          Value: lastName,
        },
        {
          Name: 'custom:organization',
          Value: organization,
        },
      ],
    });

    await cognitoClient.send(command);
  };

  const verify = async (email: string, code: string) => {
    const command = new ConfirmSignUpCommand({
      ClientId: awsconfig.aws_user_pools_web_client_id,
      Username: email,
      ConfirmationCode: code,
    });

    await cognitoClient.send(command);
  };

  const resetPassword = async (email: string) => {
    const command = new ForgotPasswordCommand({
      ClientId: awsconfig.aws_user_pools_web_client_id,
      Username: email,
    });

    await cognitoClient.send(command);
  };

  const updatePassword = async (email: string, code: string, password: string) => {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: awsconfig.aws_user_pools_web_client_id,
      Username: email,
      ConfirmationCode: code,
      Password: password,
    });

    await cognitoClient.send(command);
  };

  const updateInviteUserPassword = async (
    userID: string,
    tempPassword: string,
    newPassword: string
  ) => {
    // For invited users, set permanent password using admin API
    const command = new AdminSetUserPasswordCommand({
      UserPoolId: awsconfig.aws_user_pools_id,
      Username: userID,
      Password: newPassword,
      Permanent: true,
    });

    await cognitoClient.send(command);
    await getSession();
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'aws-sdk',
        login,
        register,
        logout,
        verify,
        resetPassword,
        updatePassword,
        updateInviteUserPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
